import { Capacitor } from "@capacitor/core"
import { Haptics, NotificationType } from "@capacitor/haptics"

import { useState, useRef } from "react"
import { useTranslation } from 'react-i18next'
import { motion } from "framer-motion"
import { Link, useNavigate } from "react-router-dom"

import {
  getParentCategories, ExpenseCategory, ExpensesErrors,
  ExpenseDocument
} from "@/types/expense"

import Button from "@/components/common/Button/Button"
import MotionText from "@/components/common/Motion/MotionText"
import MotionDiv from "@/components/common/Motion/MotionDiv"
import HeaderBack from "@/components/dashboard/Header/HeaderBack"
import DecimalInput from "@/components/common/Input/DecimalInput"
import DateInput from "@/components/common/Input/DateInput"
import FilePreview from "@/components/common/Dropzone/FilePreview"
import Select from "@/components/common/Select/Select"
import expenseAPI from "@/services/api/expense"
import Input from "@/components/common/Input/Input"
import ToggleInput from "@/components/common/Input/ToggleInput"

import { useExpensesFormStore } from "@/store/useExpensesFormStore"
import { useDashboardStore } from "@/store/useDashboardStore"

import { PiCamera, PiFile, PiInfo } from "react-icons/pi"

import { Camera, CameraResultType, CameraSource } from "@capacitor/camera"
import { base64ToFile } from "@/utils/file"
import { ocrExpense } from "@/services/ocr"
import { usePageSetup } from "@/hooks/usePageSetup"

export const ExpensesCreatePage = () => {
  const { t } = useTranslation();
  usePageSetup(t('expenses.pageTitle.add'))
  const navigate = useNavigate()

  const defaultForm = useExpensesFormStore((state) => state.form)

  const defaultFile = defaultForm.file
  const defaultCategory = defaultForm.category || '' as ExpenseCategory
  const defaultOperation = defaultForm.operation
  const defaultTotal = defaultForm.total || 0
  const defaultReference = defaultForm.reference
  const defaultDate = defaultForm.date
  const defaultPercentage = defaultForm.percentage || 100
  const defaultCalculateVatExpense = defaultForm.calculateVatExpense === undefined ? true : defaultForm.calculateVatExpense

  const [category, setCategory] = useState<ExpenseCategory>(defaultCategory)
  const [total, setTotal] = useState<number>(defaultTotal)
  const [percentage, setPercentage] = useState<number>(defaultPercentage)

  const [errors, setErrors] = useState<ExpensesErrors>({})

  const [isLoading, setIsLoading] = useState(false)
  const [isFileLoading, setIsFileLoading] = useState(false)
  const [isCameraLoading, setIsCameraLoading] = useState(false)

  const financialYear = useDashboardStore((state) => state.financialYear)
  const setExpensesForm = useExpensesFormStore((state) => state.setForm)
  const documentSignedId = useExpensesFormStore((state) => state.form.documentSignedId)

  const onDateComplete = (date: string) => {
    const year = date.split('-')[0]

    if (year !== financialYear.toString()) {
      setErrors({ date: [t('expenses.errors.yearMismatch')] })
    } else {
      setErrors((prev) => {
        const { date, ...rest } = prev
        return rest
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    try {
      const form = new FormData(e.currentTarget)

      const date = form.get('date') as string
      const cents = Math.round(total * 100)
      const reference = form.get('reference') as string
      const operation = form.get('operation') as string
      const calculateVatExpense = form.get('calculateVatExpense')?.toString() === 'on'

      if (cents < 0) {
        setIsLoading(false)
        return
      }

      await expenseAPI.create({
        expense_category_code: category,
        amount_cents: cents,
        percentage: percentage,
        date,
        operation,
        reference,
        calculate_vat_expense: calculateVatExpense,
        document_signed_id: documentSignedId
      })

      navigate("/expenses")

      document.getElementById('root')?.scrollTo(0, 0);
      Haptics.notification({ type: NotificationType.Success })
    } catch (e: any) {
      const errors = e.response?.data?.errors

      if (errors?.reference) {
        setErrors({ reference: [t('expenses.errors.referenceExists')] })
      }

      setIsLoading(false)
    }
  }

  const populateForm = async (file: File, analysis: ExpenseDocument) => {
    if (analysis) {
      const lineItems = analysis.line_items.filter((item) => item.amount > 0)
      const lineItem = lineItems[0]

      /* When bolt commission is uploaded
        There may be airport fees as well
        Sum total commission fees
      */
      if (lineItem?.category === "commission_fees") {
        const total = lineItems
          .filter((item) => item.category === "commission_fees")
          .reduce((total, item) => total + item.amount, 0)

        lineItem.amount = total
      }

      const reference = analysis?.reference ? [
        analysis?.reference.date,
        analysis?.reference.type,
        analysis?.reference.number
      ].join(', ') : ''

      await setExpensesForm({
        file: file,
        category: lineItem?.category,
        operation: lineItem?.description,
        total: lineItem?.amount,
        date: analysis?.reference?.date,
        reference: reference,
        documentSignedId: analysis?.document_signed_id
      });

      setCategory(lineItem?.category)
      setTotal(lineItem?.amount)
      onDateComplete(analysis?.reference?.date)
    }
  }

  const handleCamera = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
      });

      if (image.base64String) {
        setIsCameraLoading(true)

        const file = base64ToFile(
          image.base64String,
          `photo_${Date.now()}.${image.format}`,
          `image/${image.format}`
        );

        const analysis = await ocrExpense(file, () => {
          setIsCameraLoading(false)
        });

        if (analysis) {
          populateForm(file, analysis)
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsCameraLoading(false)
    }
  }

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    try {
      if (file) {
        setIsFileLoading(true)

        const analysis = await ocrExpense(file, () => {
          setIsFileLoading(false)
        });

        if (analysis) {
          populateForm(file, analysis)
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsFileLoading(false)
    }
  };

  return <ExpensesForm
    title={t('expenses.add')}

    handleSubmit={handleSubmit}
    handleCamera={handleCamera}
    handleFile={handleFile}

    isLoading={isLoading}
    isFileLoading={isFileLoading}
    isCameraLoading={isCameraLoading}

    file={defaultFile}
    defaultCategory={defaultCategory}
    category={category}
    defaultOperation={defaultOperation}
    defaultTotal={defaultTotal}
    total={total}
    defaultPercentage={defaultPercentage}
    percentage={percentage}
    defaultDate={defaultDate}
    defaultReference={defaultReference}
    defaultCalculateVatExpense={defaultCalculateVatExpense}

    setCategory={setCategory}
    setTotal={setTotal}
    setPercentage={setPercentage}
    onDateComplete={onDateComplete}
    errors={errors}
  />
}

interface ExpensesFormProps {
  title: string;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleDelete?: () => Promise<void>;
  handleCamera?: () => Promise<void>;
  handleFile?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;

  isLoading: boolean;
  isFileLoading?: boolean;
  isCameraLoading?: boolean;

  file?: File;
  defaultCategory?: ExpenseCategory;
  category?: ExpenseCategory;
  defaultOperation?: string;
  defaultTotal?: number;
  total: number;
  defaultPercentage?: number;
  percentage: number;
  defaultDate?: string;
  defaultReference?: string;
  defaultCalculateVatExpense?: boolean;

  setCategory: (category: ExpenseCategory) => void;
  setTotal: (total: number) => void;
  setPercentage: (percentage: number) => void;
  onDateComplete?: (date: string) => void;
  errors?: ExpensesErrors
}

export const ExpensesForm = ({
  title,
  handleDelete,
  handleSubmit,
  handleCamera,
  handleFile,

  isLoading,
  isFileLoading,
  isCameraLoading,

  file,
  defaultCategory,
  category,
  defaultOperation,
  defaultTotal,
  total,
  defaultPercentage,
  percentage,
  defaultDate,
  defaultReference,
  defaultCalculateVatExpense,

  setCategory,
  setTotal,
  setPercentage,

  onDateComplete,
  errors
}: ExpensesFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null)
  const sgid = useExpensesFormStore((state) => state.form.sgid)
  const setExpenseForm = useExpensesFormStore((state) => state.setForm)

  const persistForm = () => {
    const form = formRef.current;

    if (!form) return;

    const formData = new FormData(form);

    setExpenseForm({
      file: file,
      category: formData.get('category')?.toString() as ExpenseCategory,
      operation: formData.get('operation')?.toString(),
      total: total,
      percentage: percentage,
      date: formData.get('date')?.toString(),
      reference: formData.get('reference')?.toString(),
      calculateVatExpense: formData.get('calculateVatExpense')?.toString() === 'on',
      sgid: sgid
    });
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="h-full bg-white flex flex-col">
      <HeaderBack />
      <div className="h-full flex flex-col gap-4 px-5">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-1"
          delay={0.1}>
          {title}
        </MotionText>
        {
          file && <MotionDiv>
            <FilePreview file={file} />
          </MotionDiv>
        }
        <MotionDiv className="flex flex-col gap-5 pb-[9rem]">
          <Select
            name="category"
            label={t('expenses.category')}
            options={getParentCategories(t)}
            defaultValue={defaultCategory}
            onChange={(value) => setCategory(value as ExpenseCategory)}
          />
          <Input
            name='operation'
            defaultValue={defaultOperation}
            label={t('expenses.description')}
            required
          />
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-3">
              <DecimalInput
                name='total'
                className="w-[100%]"
                inputMode='decimal'
                label={t('expenses.amount')}
                max={9999}
                postfix="\€"
                onChange={(value) => setTotal(parseFloat(value))}
                defaultValue={defaultTotal}
                required
              />
              <DecimalInput
                name='percentage'
                className="w-[100%]"
                inputMode='decimal'
                label={t('expenses.businessPercentage')}
                placeholder="100%"
                scale={0}
                min={0}
                max={100}
                postfix="\%"
                defaultValue={defaultPercentage}
                onChange={(value) => setPercentage(parseInt(value))}
                required
                suffix={
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Link
                      className="absolute right-3 bottom-3 text-gray-400"
                      onClick={persistForm}
                      to="/expenses/percentage/info">
                      <PiInfo className='w-6 h-6' />
                    </Link>
                  </motion.div>
                }
              />
            </div>
            {total > 0 && percentage > 0 && percentage < 100 && (
              <MotionDiv
                delay={0.1}
                className="flex flex-row">
                <span className="font-medium text-xs text-zinc-700 tracking-[0.0075rem]">
                  {t('expenses.deductibleAmount', { amount: ((total * percentage) / 100).toFixed(2) + '€' })}
                </span>
              </MotionDiv>
            )}
          </div>
          {
            !handleDelete && category === "commission_fees" && (
              <MotionDiv className="flex flex-row">
                <span className="text-sm text-zinc-700 max-w-[16.9375rem]">
                  {t('expenses.calculateVat')}
                </span>
                <ToggleInput
                  name="calculateVatExpense"
                  defaultChecked={defaultCalculateVatExpense}
                />
              </MotionDiv>
            )
          }
          <DateInput
            onChange={(value) => onDateComplete?.(value)}
            name='date'
            inputMode="numeric"
            className="w-[100%]"
            label={t('expenses.date')}
            error={errors?.date?.[0]}
            placeholder={new Date().toISOString().split('T')[0]}
            defaultValue={defaultDate}
            required
          />
          <Input
            error={errors?.reference?.[0]}
            name='reference'
            defaultValue={defaultReference}
            label={t('expenses.documentReference')}
            required
            suffix={
              <motion.div whileTap={{ scale: 0.98 }}>
                <Link className="absolute right-3 bottom-3 text-gray-400"
                  onClick={persistForm}
                  to="/expenses/document/info">
                  <PiInfo className='w-6 h-6' />
                </Link>
              </motion.div>
            }
          />
        </MotionDiv>
        <div className="flex flex-row gap-2 absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%">
          <FormActions
            handleDelete={handleDelete}
            handleFile={handleFile}
            handleCamera={handleCamera}
            isLoading={isLoading}
            isFileLoading={isFileLoading}
            isCameraLoading={isCameraLoading}
          />
        </div>
      </div>
    </form>
  )
}

interface FormActionsProps {
  handleDelete?: () => Promise<void>,
  handleFile?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>,
  handleCamera?: () => Promise<void>,

  isLoading: boolean,
  isFileLoading?: boolean,
  isCameraLoading?: boolean,
}

const FormActions = ({
  handleDelete,
  handleFile,
  handleCamera,
  isLoading,
  isFileLoading,
  isCameraLoading,
}: FormActionsProps) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (handleDelete) {
    return <>
      <Button
        key="delete"
        className="h-12"
        variant="secondary"
        onClick={handleDelete}
      >
        <span>{t('common.delete')}</span>
      </Button>
      <Button
        key="submit"
        type="submit"
        isLoading={isLoading}
        className="h-12 w-full"
      >
        <span>{t('common.save')}</span>
      </Button>
    </>
  }

  return (
    <div className="flex flex-row w-full gap-2 relative pr-20">
      <Button type="submit" className="h-12 w-full" isLoading={isLoading}>
        <span>{t('common.save')}</span>
      </Button>
      <div className="flex flex-col gap-2 absolute right-0 bottom-0">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFile}
          accept={".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"}
          />
        <Button
          variant="secondary"
          className="h-12"
          isLoading={isFileLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          <PiFile className="w-6 h-6" />
        </Button>
        {Capacitor.isNativePlatform() && <Button
          className="h-12"
          isLoading={isCameraLoading}
          onClick={handleCamera}
        >
          <PiCamera className="w-6 h-6" />
        </Button>}
      </div>
    </div>
  )
}

export default ExpensesCreatePage